import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { usePushEvents } from '../../../../hooks/PushEvents';
import { Donee, useDonee, useDonorProfile } from '../../../../api';
import {
    getDoneeAmountDeepLink,
    useDeepLink,
} from '../../../../hooks/useDeepLink';
import { copyDoneeIdToClipboard } from '../../../../utils/clipboard';
import { isAppleDevice } from '../common/utils';
import { appLinks } from '../../../../utils/webConfig';
import {
    clickEvent,
    successEvent,
    viewEvent,
} from '../../../../hooks/PushEvents/clevertapEvents';
import DeepLinkCopiedModal from './ThankYouWizard/DeepLinkCopiedModal';
import { LockIcon, FlashIcon, SmallVerifiedIcon } from '../../../icons';
import { Text } from '../common/Text/Text';
import { colors } from '../../../theme';
import GivelifyBottomSheet from '../common/Modal/GivelifyBottomSheet';
import { Button } from '../../../ui';

const getSubTitle = (donee: Donee | undefined) => (
    <>
        When you open the app, tap “Allow Paste”
        {donee && (
            <>
                <br />
                to get to {donee.organization.name}
            </>
        )}
    </>
);

const postEventPayload = {
    action: 'clicked',
    location: 'ClipboardPasteBottomSheet',
    screen: 'GiftAmount',
    voucher: 'DDL voucher',
    name: 'GoToAppStore',
};

const localStorageKey = 'information-banner';
const closeInformationBanner = () => {
    localStorage.setItem(localStorageKey, new Date().toISOString());
};
export const showInformationBanner = ((): boolean => {
    const date = localStorage.getItem(localStorageKey);
    // if (!date) return true;
    // const now = new Date();
    // const then = new Date(date);
    // const diff = now.getTime() - then.getTime();
    // const hours = diff / 1000 / 60 / 60;
    // return hours < 24;
    return !date;
})();

const viewCopyEventPayload = {
    location: 'GiftAmount',
    action: 'view',
    name: 'ClipboardPasteBottomSheet',
};
const viewEventPayload = {
    location: 'GiftAmount',
    action: 'view',
    name: 'GPTrustBar',
};
const eventPayloadTrust = {
    location: 'GiftAmount',
    action: 'clicked',
    name: 'GPTrustBar',
};
const eventPayloadClose = {
    location: 'GiftAmount',
    action: 'clicked',
    name: 'ContinueInBrowser',
};
const eventPayload = {
    location: 'GiftAmount',
    action: 'clicked',
    name: 'GiveOnApp',
};
const link = appLinks.mainMobileBanner;
const handleDownload = () => {
    window.location.href = isAppleDevice() ? link.apple : link.android;
};

interface MainMobileBannerProps {
    handleClose: () => unknown;
}

const StyledInfo = styled.div<{ marginBottom?: string }>`
    gap: 16px;
    display: flex;
    ${({ marginBottom }) => marginBottom && `margin-bottom: ${marginBottom};`}
    .info-icon {
        padding: 5px;
    }
    ul {
        font-size: 12px;
        list-style: inherit;
        padding-left: 16px;
        color: ${colors.primarySecondaryContent};
    }
    .info-text {
        .info-title {
            margin-bottom: 4px;
        }
        .info-description {
            margin-bottom: 6px;
        }
    }
`;

const Info: React.FC<{
    title: string;
    description: string;
    points: string[];
    icon: React.ReactNode;
    marginBottom?: string;
}> = ({ title, description, points, icon, marginBottom }) => {
    return (
        <StyledInfo marginBottom={marginBottom}>
            <div className="info-icon">{icon}</div>
            <div className="info-text">
                <Text
                    weight="extraBold"
                    className="info-title"
                >
                    {title}
                </Text>
                <Text
                    variant="small"
                    className="info-description"
                >
                    {description}
                </Text>
                <ul>
                    {points.map((point, index) => (
                        <li key={index}>{point}</li>
                    ))}
                </ul>
            </div>
        </StyledInfo>
    );
};

const StyledDiv = styled.div`
    .information-banner {
        display: flex;
        cursor: pointer;
        padding-top: 16px;
        padding-bottom: 16px;
        margin-bottom: 16px;
        justify-content: space-between;
        border-radius: 20px;
        border: 1px solid ${colors.utilitySeparatorColor};
        background-color: ${colors.utilityBackgroundLightColor};
        & > div {
            padding-left: 16px;
            padding-right: 16px;
            gap: 6px;
            flex: 1;
            display: flex;
            align-items: center;
            flex-direction: column;
            .information-banner-text {
                text-align: center;
            }
        }
        .instant-gift {
            border-left: 1px solid ${colors.utilitySeparatorColor};
            border-right: 1px solid ${colors.utilitySeparatorColor};
        }
    }
    .information-banner-continue-text {
        text-align: center;
        margin-bottom: 16px;
        .underline {
            cursor: pointer;
            text-decoration: underline;
        }
    }
`;

const MainMobileBanner: React.FC<MainMobileBannerProps> = ({ handleClose }) => {
    const { donee } = useDonee();
    const { pushEvent } = usePushEvents();
    const { donorProfile } = useDonorProfile();
    const [url, setUrl] = useState<string | undefined>(undefined);
    const [showMoreInfo, setShowMoreInfo] = useState<boolean>(false);

    useEffect(() => {
        pushEvent(viewEvent, viewEventPayload);
    }, [pushEvent]);

    const handleShowMoreInfo = useCallback(() => {
        pushEvent(clickEvent, eventPayloadTrust);
        setShowMoreInfo(true);
    }, [pushEvent, setShowMoreInfo]);

    const handleHideMoreInfo = useCallback(() => {
        pushEvent(clickEvent, { ...eventPayloadTrust, action: 'close' });
        setShowMoreInfo(false);
    }, [pushEvent, setShowMoreInfo]);

    const handleCloseBanner = useCallback(() => {
        pushEvent(clickEvent, eventPayloadClose);
        closeInformationBanner();
        handleClose();
    }, [handleClose, pushEvent]);

    const handleDownloadClicked = useCallback(() => {
        pushEvent(clickEvent, eventPayload);
        copyDoneeIdToClipboard(donee?.organization.doneeId);
        handleDownload();
    }, [donee, pushEvent]);

    const {
        deepLinkState,
        showCopiedModal,
        getDeepLink,
        isDeferredDeepLinkOn,
        setShowCopiedModal,
    } = useDeepLink(clickEvent, eventPayload, handleDownload);

    const handleDownloadApp = useCallback(() => {
        pushEvent(clickEvent, eventPayload);
        getDeepLink(
            donee,
            donorProfile,
            getDoneeAmountDeepLink(donee),
            undefined,
        ).then((s) => {
            s && setUrl(s);
        });
    }, [donee, donorProfile, getDeepLink, pushEvent]);

    return (
        <StyledDiv>
            <div
                className="information-banner"
                onClick={handleShowMoreInfo}
            >
                <div className="verified-organization">
                    <SmallVerifiedIcon />
                    <Text
                        variant="xSmall"
                        className="information-banner-text"
                    >
                        Verified Organization
                    </Text>
                </div>
                <div className="instant-gift">
                    <FlashIcon />
                    <Text
                        variant="xSmall"
                        className="information-banner-text"
                    >
                        Instant Gift Delivery
                    </Text>
                </div>
                <div className="encrypted-security">
                    <LockIcon />
                    <Text
                        variant="xSmall"
                        className="information-banner-text"
                    >
                        Encrypted Security
                    </Text>
                </div>
            </div>
            <Button
                id="information-banner-button"
                variant="give"
                disabled={Boolean(deepLinkState.voucher)}
                loading={deepLinkState.loading}
                onClick={
                    isDeferredDeepLinkOn()
                        ? handleDownloadApp
                        : handleDownloadClicked
                }
                className="mb-6 w-full"
            >
                Give on the Givelify App
            </Button>
            <Text
                variant="small"
                color="primarySecondaryContent"
                className="information-banner-continue-text"
            >
                Or{' '}
                <span
                    tabIndex={0}
                    role="button"
                    className="underline"
                    onClick={handleCloseBanner}
                >
                    continue in your browser
                </span>
            </Text>
            <DeepLinkCopiedModal
                url={url}
                clickEvent={clickEvent}
                viewEvent={viewEvent}
                successEvent={successEvent}
                open={showCopiedModal}
                clickEventPayload={postEventPayload}
                viewEventPayload={viewCopyEventPayload}
                title="Get the free Givelify app"
                subTitle={getSubTitle(donee)}
                onClick={handleDownload}
                onClose={() => setShowCopiedModal(false)}
            />
            {showMoreInfo && (
                <GivelifyBottomSheet onClosedCallback={handleHideMoreInfo}>
                    <Info
                        marginBottom="32px"
                        title="Verified Organization"
                        description="Give with confidence"
                        points={[
                            'Thoroughly vetted by Givelify',
                            'Verified as tax-deductible',
                            'Identity has been confirmed',
                        ]}
                        icon={<SmallVerifiedIcon />}
                    />
                    <Info
                        marginBottom="32px"
                        title="Instant Gift Delivery"
                        description="Lightning fast generosity"
                        points={[
                            'Gifts go directly to the organization',
                            'They’re notified right when you give',
                        ]}
                        icon={<FlashIcon />}
                    />
                    <Info
                        title="Encrypted Security"
                        description="Give with peace of mind"
                        points={[
                            'Credit card and personal details are protected with AES 256-bit encryption',
                            'Security is the same standard used by banks, governments, and military',
                        ]}
                        icon={<LockIcon />}
                    />
                </GivelifyBottomSheet>
            )}
        </StyledDiv>
    );
};

export default MainMobileBanner;
